'use client';

import { useQueryClient } from '@tanstack/react-query';
import { usePathname, useRouter } from 'next/navigation';
import { checkLogin } from 'src/utils/auth';

import MissionListItem from '@/app/(football)/[team]/_components/common/MissionListItem';
import EarnSuccessModal from '@/app/(football)/[team]/point-lounge/_components/Modal/EarnSuccessModal';
import OfflineEventConfirmModal from '@/app/(football)/[team]/point-lounge/_components/Modal/OfflineEventConfirmModal';
import { trackEvent } from '@/libs/analytics/events';
import { useAppDispatch } from '@/store/client/config';
import { modalActions } from '@/store/client/slices/modalSlice';
import { USE_FOOTBALL_QUERY_KEY, useSavePointMutation } from '@/store/server/common/football/football.query';
import { USE_POINT_QUERY_KEY } from '@/store/server/common/point/point.query';

const POINT_METHOD_CODES = {
  CLICK: { code: 'CLICK' },
  OFFLINE: { code: 'OFFLINE' },
} as const;

interface EarnMissionListItemProps {
  title: string;
  image: string;
  methodIdx: number;
  methodCode: string;
  link?: string;
  point: number;
  offlineYn?: 'Y' | 'N';
  hasCompleted?: boolean;
}

export default function EarnMissionListItem({
  title,
  image,
  methodIdx,
  methodCode,
  link,
  point,
  offlineYn,
  hasCompleted,
}: EarnMissionListItemProps) {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const router = useRouter();
  const pathname = usePathname();

  const { mutate: savePoint, isPending: isSaving } = useSavePointMutation({
    onSuccess: () => {
      trackEvent({ name: 'earning_point', params: { point, value: point, action_type: methodCode } });

      dispatch(
        modalActions.openComponentModal({
          component: <EarnSuccessModal point={point} />,
        }),
      );

      queryClient.invalidateQueries({ queryKey: [USE_FOOTBALL_QUERY_KEY.getPointSave] });
      queryClient.invalidateQueries({ queryKey: [USE_FOOTBALL_QUERY_KEY.getRealTimeEdition] });
      queryClient.invalidateQueries({ queryKey: [USE_POINT_QUERY_KEY.getMyPoint] });
    },
  });

  const handleClick = () => {
    if (!checkLogin()) {
      dispatch(
        modalActions.openConfirmModal({
          icon: 'danger',
          title: ['포인트 라운지를 이용하려면', '로그인해주세요.'],
          confirmText: ['로그인하기'],
          closeText: ['확인'],
          onConfirm: () => {
            router.push(`/account/login?retUrl=${pathname}`);
          },
        }),
      );
      return;
    }

    if (!hasCompleted) {
      if (methodCode === POINT_METHOD_CODES.CLICK.code && link) {
        window.open(link, '_blank');
      }
      savePoint({ methodCode, methodIdx });
      return;
    }

    if (hasCompleted && methodCode === POINT_METHOD_CODES.OFFLINE.code && offlineYn === 'N') {
      dispatch(
        modalActions.openComponentModal({
          component: <OfflineEventConfirmModal methodIdx={methodIdx} methodCode={methodCode} />,
        }),
      );
    }
  };

  const isDisabled =
    (methodCode !== POINT_METHOD_CODES.OFFLINE.code && hasCompleted) ||
    (methodCode === POINT_METHOD_CODES.OFFLINE.code && hasCompleted && offlineYn === 'Y');

  return (
    <MissionListItem
      title={title}
      image={image}
      buttonText={`${point.toLocaleString('ko-KR')}P`}
      disabled={isDisabled || isSaving}
      onClick={handleClick}
    />
  );
}
